import {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	type ReactNode,
} from "react";
import {
	sha256,
	getStoredAuth,
	setStoredAuth,
	clearStoredAuth,
	type StoredAuth,
} from "../lib/auth";
import { apiFetch } from "../lib/api";

interface AuthState {
	accountName: string | null;
	accountId: string | null;
	isAuthenticated: boolean;
	isLoading: boolean;
}

interface AuthContextType extends AuthState {
	checkStatus: (
		accountName: string,
	) => Promise<"initialized" | "not_initialized">;
	login: (accountName: string, password: string) => Promise<void>;
	register: (accountName: string, password: string) => Promise<void>;
	logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<AuthState>({
		accountName: null,
		accountId: null,
		isAuthenticated: false,
		isLoading: true,
	});

	useEffect(() => {
		const stored = getStoredAuth();
		if (stored) {
			setState({
				accountName: stored.accountName,
				accountId: stored.accountId,
				isAuthenticated: true,
				isLoading: false,
			});
		} else {
			setState((s) => ({ ...s, isLoading: false }));
		}
	}, []);

	const checkStatus = useCallback(
		async (
			accountName: string,
		): Promise<"initialized" | "not_initialized"> => {
			const id = await sha256(accountName);
			try {
				await apiFetch(`/api/userspace/${id}/status`);
				return "initialized";
			} catch {
				return "not_initialized";
			}
		},
		[],
	);

	const login = useCallback(
		async (accountName: string, password: string) => {
			const id = await sha256(accountName);
			await apiFetch(`/api/userspace/${id}/login`, {
				method: "POST",
				body: JSON.stringify({ password }),
			});
			const auth: StoredAuth = { accountName, accountId: id };
			setStoredAuth(auth);
			setState({
				accountName,
				accountId: id,
				isAuthenticated: true,
				isLoading: false,
			});
		},
		[],
	);

	const register = useCallback(
		async (accountName: string, password: string) => {
			const id = await sha256(accountName);
			await apiFetch(`/api/userspace/${id}/register`, {
				method: "POST",
				body: JSON.stringify({ password }),
			});
			const auth: StoredAuth = { accountName, accountId: id };
			setStoredAuth(auth);
			setState({
				accountName,
				accountId: id,
				isAuthenticated: true,
				isLoading: false,
			});
		},
		[],
	);

	const logout = useCallback(() => {
		clearStoredAuth();
		setState({
			accountName: null,
			accountId: null,
			isAuthenticated: false,
			isLoading: false,
		});
	}, []);

	return (
		<AuthContext.Provider
			value={{ ...state, checkStatus, login, register, logout }}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used within AuthProvider");
	return ctx;
}
